import {
  GqlMeeting,
  GqlMeetingKind,
  GqlSnrAuthorization,
  GqlWarrantProgram,
  GqlWarrantProgramKind,
} from '../../../../../graphql/base-types.graphql';
import { HandleCreateMeetingFn, StepMeeting } from '../../../../common/components/StepMeeting';
import { WarrantProgramDocument } from '../../../WarrantProgram.graphql';
import {
  useWpConnectMeetingMutation,
  useWpCreateMeetingMutation,
  useWpDisconnectMeetingMutation,
} from '../../../../../graphql/react-operations';
import { Stack } from '@startuptools/ui/Stack';
import { Box } from '@startuptools/ui/Box';
import { Button } from '@startuptools/ui/Button';
import { Trans } from '@lingui/react';
import { wpEditDocumentsLink } from '../../../../../components/Links';
import { useWpConnectAuthorizationMutation, useWpDisconnectAuthorizationMutation } from './StepMeeting.graphql';
import { DisplayDocument } from '@startuptools/business-ui/document/Document';
import { useStepper } from '../../../../../components/steppers/common/StepperContext';
import { DateTime } from 'luxon';
import { useNavigate } from '../../../../../components/Navigate';

export const WpStepMeeting = ({
  warrantProgram,
  meeting,
  snrAuth,
  hasActiveSubscription,
}: {
  warrantProgram: Pick<GqlWarrantProgram, 'id' | 'companyId' | 'usesStepper' | 'warrantKind' | 'warrantStartDate'>;
  meeting:
    | (Pick<GqlMeeting, 'id' | 'kind' | 'number' | 'heldKind' | 'startTime' | 'usesStepper'> & {
        latestMinutesDocument?: DisplayDocument | null;
      })
    | null
    | undefined;
  snrAuth:
    | Pick<GqlSnrAuthorization, 'id' | 'registrationDate' | 'filingNumber' | 'filingYear' | 'auths'>
    | null
    | undefined;
  hasActiveSubscription: boolean;
}) => {
  const { stepNext } = useStepper();
  const { navigate } = useNavigate();
  const [doCreate, { loading: loadingCreate }] = useWpCreateMeetingMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });
  const [doConnect, { loading: loadingConnect }] = useWpConnectMeetingMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });
  const [doDisconnect, { loading: loadingDisconnect }] = useWpDisconnectMeetingMutation({
    refetchQueries: [WarrantProgramDocument],
    awaitRefetchQueries: true,
  });
  // eslint-disable-next-line sut/loading-mutation
  const [doConnectSnrAuth] = useWpConnectAuthorizationMutation({ refetchQueries: [WarrantProgramDocument] });
  // eslint-disable-next-line sut/loading-mutation
  const [doDisconnectSnrAuth] = useWpDisconnectAuthorizationMutation({ refetchQueries: [WarrantProgramDocument] });

  const allowMeetingKinds =
    warrantProgram.warrantKind === GqlWarrantProgramKind.Employee
      ? [GqlMeetingKind.BoardMeeting]
      : [GqlMeetingKind.ExtraShareholdersMeeting, GqlMeetingKind.ShareholdersMeeting, GqlMeetingKind.BoardMeeting];

  const handleCreateMeeting: HandleCreateMeetingFn = ({ endTime, startTime, ...values }) => {
    return doCreate({
      variables: {
        companyId: warrantProgram.companyId,
        input: {
          id: warrantProgram.id,
          meetingCreate: {
            startTime: startTime.toUTC(0, { keepLocalTime: true }).toString(),
            endTime: endTime?.toUTC(0, { keepLocalTime: true }).toString(),
            ...values,
          },
        },
      },
    });
  };

  const handleSelectAuth = async (snrAuthorizationId: string) => {
    return doConnectSnrAuth({
      variables: { companyId: warrantProgram.companyId, input: { id: warrantProgram.id, snrAuthorizationId } },
    });
  };

  const handleDisconnectAuth = async () => {
    return doDisconnectSnrAuth({
      variables: { companyId: warrantProgram.companyId, input: { id: warrantProgram.id } },
    });
  };

  return (
    <Stack gap={3}>
      <StepMeeting
        loadingStates={{
          create: loadingCreate,
          connect: loadingConnect,
          disconnect: loadingDisconnect,
        }}
        dateSettings={{
          meetingDate: warrantProgram.warrantStartDate
            ? DateTime.fromISO(warrantProgram.warrantStartDate).set({ hour: 12 })
            : undefined,
        }}
        meeting={meeting}
        snrAuth={snrAuth}
        allowMeetingKinds={allowMeetingKinds}
        hasActiveSubscription={hasActiveSubscription}
        handleCreateMeeting={handleCreateMeeting}
        handleConnectMeeting={meetingId =>
          doConnect({ variables: { companyId: warrantProgram.companyId, input: { id: warrantProgram.id, meetingId } } })
        }
        handleDisconnectMeeting={() =>
          doDisconnect({ variables: { companyId: warrantProgram.companyId, input: { id: warrantProgram.id } } })
        }
        handleSelectAuth={warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee ? handleSelectAuth : null}
        handleDisconnectAuth={
          warrantProgram.warrantKind !== GqlWarrantProgramKind.Employee ? handleDisconnectAuth : null
        }
      />

      <Box>
        <Button onClick={() => stepNext(() => navigate(wpEditDocumentsLink(warrantProgram)))}>
          <Trans id="Continue" />
        </Button>
      </Box>
    </Stack>
  );
};
